import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { 
  Users, TrendingUp, Calendar, DollarSign, 
  CheckCircle, Clock, AlertCircle, RefreshCw 
} from 'lucide-react';
import { efetividadeService } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Efetividade = () => {
  const [periodo, setPeriodo] = useState('30');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  
  const queryClient = useQueryClient();

  // Buscar dados de efetividade
  const { data: efetividades, isLoading } = useQuery({
    queryKey: ['efetividade', periodo],
    queryFn: () => efetividadeService.getEfetividade({ periodo }),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Calcular efetividade
  const calcularMutation = useMutation({
    mutationFn: efetividadeService.calcularEfetividade,
    onSuccess: () => {
      queryClient.invalidateQueries(['efetividade']);
    },
  });

  const handleCalcularEfetividade = () => {
    if (!dataInicio || !dataFim) {
      alert('Selecione as datas de início e fim');
      return;
    }
    
    calcularMutation.mutate({ data_inicio: dataInicio, data_fim: dataFim });
  };

  // Preparar dados para gráficos
  const dadosGrafico = efetividades?.map(item => ({
    nome: item.usuario_nome,
    primeira: item.primeira_cobranca,
    segunda: item.segunda_cobranca,
    terceira: item.terceira_cobranca,
    quarta: item.quarta_cobranca,
    quinta: item.quinta_cobranca,
    sexta_plus: item.sexta_plus_cobranca,
    geral: item.percentual_geral,
  })) || [];

  const dadosPizza = efetividades?.map(item => ({
    name: item.usuario_nome,
    value: item.percentual_geral,
  })) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Efetividade de Cobrança
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Análise de efetividade por funcionário e tipo de cobrança
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
            <option value="180">Últimos 180 dias</option>
          </select>
          
          <button
            onClick={() => queryClient.invalidateQueries(['efetividade'])}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Filtros de Data */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Calcular Efetividade</h3>
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Data Início
            </label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Data Fim
            </label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <button
            onClick={handleCalcularEfetividade}
            disabled={calcularMutation.isPending}
            className="mt-6 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {calcularMutation.isPending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <TrendingUp className="w-4 h-4" />
            )}
            <span>Calcular</span>
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Funcionários
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {efetividades?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Efetividade Média
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {efetividades?.length > 0 
                  ? `${(efetividades.reduce((acc, item) => acc + item.percentual_geral, 0) / efetividades.length).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Fechadas
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {efetividades?.reduce((acc, item) => acc + item.total_contas_fechadas, 0) || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Valor Recebido
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(efetividades?.reduce((acc, item) => acc + item.valor_total_recebido, 0) || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de Barras */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Efetividade por Tipo de Cobrança</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={dadosGrafico}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="nome" />
            <YAxis />
            <Tooltip formatter={(value) => `${value}%`} />
            <Legend />
            <Bar dataKey="primeira" fill="#0088FE" name="1ª Cobrança" />
            <Bar dataKey="segunda" fill="#00C49F" name="2ª Cobrança" />
            <Bar dataKey="terceira" fill="#FFBB28" name="3ª Cobrança" />
            <Bar dataKey="quarta" fill="#FF8042" name="4ª Cobrança" />
            <Bar dataKey="quinta" fill="#8884D8" name="5ª Cobrança" />
            <Bar dataKey="sexta_plus" fill="#82CA9D" name="6ª+ Cobrança" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Gráfico de Pizza */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Efetividade Geral por Funcionário</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dadosPizza}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {dadosPizza.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tabela de Detalhes */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Detalhes por Funcionário</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Funcionário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Geral
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fechadas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Valor
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {efetividades?.map((item) => (
                  <tr key={item.usuario_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {item.usuario_nome}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.percentual_geral >= 80 ? 'bg-green-100 text-green-800' :
                        item.percentual_geral >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.percentual_geral}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {item.total_contas_fechadas}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCurrency(item.valor_total_recebido)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Efetividade; 